import {Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {forkJoin} from 'rxjs';
import {createMemo} from '../../../memo';
import {
  faChevronLeft,
  faCircleArrowRight,
  faCircleInfo,
  faCube,
  faFolderOpen,
  faHouse,
  faPlus,
  faStop,
  faTriangleExclamation,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import {StateConfigService} from "../../../services/state/state-config.service";
import {ChatService, ChatSurface, FileReference, UiBlock} from "../service/chat.service";
import {Router} from "@angular/router";
import {
  BimBaseCheckControllerService,
  CheckResultControllerService,
  HoaiCheckControllerService,
  IfcControllerService,
  ProjectFile,
  QualityControllerService,
  SimpleCheck
} from "../../../api";
import { DatePipe, Location, NgClass } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicInputComponent,
  BicPopupComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {
  faBadgeCheck,
  faChartPieSimple,
  faChevronRight,
  faClipboardCheck,
  faFile,
  faFilePdf,
  faFolderOpen as faFolderOpenSolid,
  faGear,
  faGrid2,
  faMemoCircleCheck,
  faPaperPlaneTop,
} from "@fortawesome/pro-solid-svg-icons";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {faChatbicAi, faIfc, faReport} from "../../util/icons/custom-icons";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {UiBlockPanelComponent} from '../ui-block-panel/ui-block-panel.component';
import {CheckReportService} from '../../../services/checks/check-report.service';
import {ContextFilePickerComponent} from '../../util/context-file-picker/context-file-picker.component';
import {ProposalReviewService} from '../../../services/chat/proposal-review.service';

@Component({
  selector: 'app-message-view',
  templateUrl: './message-view.component.html',
  styleUrls: ['./message-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, NgClass, FaIconComponent, DatePipe, BicPopupComponent, BicButtonComponent, BicInputComponent, BicBadgeComponent, UiBlockPanelComponent, TooltipDirective, ContextFilePickerComponent]
})
export class MessageViewComponent implements OnInit, OnDestroy {
  protected chatService = inject(ChatService);
  protected proposalReview = inject(ProposalReviewService);
  private router = inject(Router);
  private location = inject(Location);
  private ifcCheckController = inject(IfcControllerService);
  private bimBaseController = inject(BimBaseCheckControllerService);
  protected stateConfigService = inject(StateConfigService);

  @ViewChild('textareaRef') textareaRef!: ElementRef;
  bimcheckChipHovering = false;
  loicheckChipHovering = false;
  hoaiChipHovering = false;
  qualityChipHovering = false;
  fileChipHovering = false;
  //popup related
  showFolderPopup = false;
  selectedModule: any = null;
  //needed for modules like BIM, LOI, HOAI where you have to select a specific check after selecting the module
  selectedModuleItem: any = null;
  /** Dateiauswahl (`app-context-file-picker`): im Quickstart direkt über „+", sonst über „Dateien". */
  showFilePicker = false;
  hoaiChecks: SimpleCheck[] = [];

  loiChecks: SimpleCheck[] = [];
  bimChecks: SimpleCheck[] = [];
  qualityChecks: SimpleCheck[] = [];
  myCheckRuns: SimpleCheck[] = [];
  private checkResultService = inject(CheckResultControllerService);
  private checkReportService = inject(CheckReportService);
  private readonly allModules = [
    {key: 'FILES', label: 'Dateien', icon: faFolderOpen, innerIcon: faFilePdf},
    // myCheck-Prüfergebnisse: chatBIC liest dann gezielt deren Befunde (ChatContext.checkRuns).
    {key: 'RUNS', label: 'Prüfergebnisse (myCheck)', icon: faFolderOpen, innerIcon: faMemoCircleCheck},
    // Ältere Prüfungen aus der Zeit vor myCheck. Den geöffneten myCheck-Bericht kennt chatBIC über
    // die Route; eine Auswahl braucht es dafür nicht.
    {key: 'LOI', label: 'LOI-Check (ältere Prüfung)', icon: faFolderOpen, innerIcon: faMemoCircleCheck},
    {key: 'BIM', label: 'BIM-Basis-Check (ältere Prüfung)', icon: faFolderOpen, innerIcon: faMemoCircleCheck},
    {key: 'HOAI', label: 'HOAI-Check (ältere Prüfung)', icon: faFolderOpen, innerIcon: faMemoCircleCheck},
    {key: 'All-Right', label: 'All-Right-Check (ältere Prüfung)', icon: faFolderOpen, innerIcon: faMemoCircleCheck}
  ];
  private readonly memo = createMemo();

  /**
   * Die wählbaren Kontext-Module. Im myCheck-Quickstart nur Dateien: der Agent legt dort erst die
   * Prüfung an, es gibt noch keine Prüfergebnisse, über die er sprechen könnte.
   */
  get modules() {
    return this.memo('modules', [this.surface],
      () => this.surface === 'mycheck' ? this.allModules.filter(m => m.key === 'FILES') : this.allModules);
  }

  /** Maps route modules to their sidebar icons */
  private readonly moduleIcons: Record<string, IconDefinition> = {
    'dashboard': faGrid2,
    'files': faFolderOpenSolid,
    'tasks': faClipboardCheck,
    'quality': faBadgeCheck,
    'all-in-check': faChartPieSimple,
    'reports': faReport as IconDefinition,
    'ifc-viewer': faIfc as IconDefinition,
    'settings': faGear,
    'chat': faChatbicAi as IconDefinition,
    [ChatService.VIEWER_MODULE]: faIfc as IconDefinition,
  };

  getContextIcon(): IconDefinition {
    return this.moduleIcons[this.chatService.currentPlatformContext.module] || faHouse;
  }

  /**
   * Welche Chatfläche diese Eingabe bedient.
   *
   * <p>Muss vor der Registrierung bekannt sein, deshalb passiert die in `ngOnInit` und nicht mehr
   * im Konstruktor: dort ist der Input noch nicht gesetzt, und mit zwei gemounteten Ansichten
   * gewann die zuletzt konstruierte – der Ordner-Dialog landete in einer abgehängten Komponente.
   */
  @Input() surface: ChatSurface = 'classic';

  /** Der Zustand der Fläche, die diese Eingabe bedient. */
  get session() {
    return this.chatService.session(this.surface);
  }

  /**
   * Badge des Objekts, auf dem der Nutzer steht. Im Edit-Chat entfällt es, solange oben der
   * Kontext-Chip (#2789) denselben myCheck nennt – zweimal derselbe Name sagt nichts.
   */
  showContextEntityBadge(): boolean {
    return !!this.chatService.contextEntityLabel
      && !(this.surface === 'edit' && this.proposalReview.contextText());
  }

  /** Was gerade im 3D-Viewer offen ist – zeigt dem Nutzer, dass chatBIC das Modell kennt. */
  protected get viewerContext() {
    return this.chatService.viewerContext;
  }

  /** „+2 Modelle" beim Koordinationsmodell. */
  protected get viewerModelSuffix(): string {
    const additional = (this.chatService.viewerContext?.models.length ?? 0) - 1;
    return additional > 0 ? ` +${additional} Modelle` : '';
  }

  protected readonly faIfc = faIfc as IconDefinition;
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faFilePdf = faFilePdf;
  private hoaiCheckController = inject(HoaiCheckControllerService);
  private qualityControllerService = inject(QualityControllerService);
  private dataPreloaded = false;
  isLoadingData = false;

  ngOnInit() {
    // Erst hier, nicht im Konstruktor: der `surface`-Input ist dort noch nicht gesetzt.
    this.chatService.setMessageViewComponent(this.surface, this);
    this.preloadData();
  }

  ngOnDestroy() {
    this.chatService.clearMessageViewComponent(this.surface, this);
  }

  onPlusButtonClick() {
    // Im Quickstart gibt es nur Dateien als Kontext: direkt in den Picker, ohne Modulliste.
    if (this.surface === 'mycheck') {
      this.showFilePicker = true;
      return;
    }
    this.showFolderPopup = true;
    this.selectedModule = null;
    this.selectedModuleItem = null;
    // Die Prüfungslisten (RUNS, LOI, BIM, …); die Dateien lädt der Picker selbst.
    if (!this.dataPreloaded) {
      this.loadFolderData();
    }
  }

  preloadData() {
    if (this.chatService.currentProject) {
      this.loadFolderData();
    }
  }

  protected readonly faSpinnerThird = faSpinnerThird;

  navigateIntoModule(module: any) {
    if (module.key === 'FILES') {
      this.closeFolderPopup();
      this.showFilePicker = true;
      return;
    }
    this.selectedModule = module;
    this.selectedModuleItem = null;
  }

  onModuleItemClick(item: any) {
    this.selectedModuleItem = item;
  }

  isModuleItemSelected(item: any) {
    return this.selectedModuleItem && this.selectedModuleItem.id === item.id;
  }

  /** Bereits gewählte Kontextdateien – der Picker zeigt sie als „bereits hinzugefügt". */
  get selectedFileIds(): string[] {
    return this.session.selectedFiles.map(f => f.id);
  }

  /**
   * Übernimmt die im Picker gewählten Dateien. Die Auswahl ergänzt die bisherigen Kontextdateien
   * (nach ID dedupliziert, das erste Vorkommen gewinnt – doppelte IDs lösen in den Chips NG0955 aus);
   * Prüfungs-Kontexte bleiben stehen.
   */
  onContextFilesPicked(files: ProjectFile[]): void {
    const picked: FileReference[] = files
      .filter(file => !!file.id)
      .map(file => ({id: file.id!, dataId: file.id!, fileName: file.name ?? ''}));
    const seen = new Set<string>();
    this.session.selectedFiles = [...this.session.selectedFiles, ...picked].filter(file => {
      if (!file.id || seen.has(file.id)) return false;
      seen.add(file.id);
      return true;
    });
    this.showFilePicker = false;
  }

  /**
   * Picker ohne Auswahl geschlossen („Abbrechen", X, Ladefehler). Im klassischen Chat kam der Nutzer
   * über die Modulliste in den Picker und landet wieder dort; im Quickstart öffnet „+" den Picker
   * direkt, dort schließt Abbrechen nur den Picker.
   */
  onFilePickerClosed(): void {
    this.showFilePicker = false;
    if (this.surface !== 'mycheck') {
      this.onPlusButtonClick();
    }
  }

  loadFolderData() {
    this.isLoadingData = true;

    const loiChecks$ = this.ifcCheckController.getChecks1(this.chatService.currentProject);
    const bimChecks$ = this.bimBaseController.getChecks2(this.chatService.currentProject);
    const hoaiChecks$ = this.hoaiCheckController.getHOAIChecks(this.chatService.currentProject);
    const qualityChecks$ = this.qualityControllerService.getChecks(this.chatService.currentProject);
    const runs$ = this.checkResultService.getCheckResultsAsSimpleChecks(this.chatService.currentProject, 'ALL');

    forkJoin([loiChecks$, bimChecks$, hoaiChecks$, qualityChecks$, runs$]).subscribe({
      next: ([loiChecks, bimChecks, hoaiChecks, qualityChecks, runs]) => {
        this.loiChecks = loiChecks;
        this.bimChecks = bimChecks;
        this.hoaiChecks = hoaiChecks;
        this.qualityChecks = qualityChecks;
        // Nur abgeschlossene myCheck-Läufe mit Bericht – ein laufender oder fehlgeschlagener hat
        // keine Befunde, über die chatBIC sprechen könnte.
        this.myCheckRuns = runs.filter(run => run.checkType === 'MY_CHECK' && run.completed
          && this.checkReportService.canOpenReport(run));
        this.dataPreloaded = true;
        this.isLoadingData = false;
      },
      error: () => {
        this.isLoadingData = false;
      }
    });
  }

  getModuleItems() {
    if (!this.selectedModule) return [];
    switch (this.selectedModule.key) {
      case 'BIM': return this.bimChecks;
      case 'LOI': return this.loiChecks;
      case 'HOAI':
        return this.hoaiChecks;
      case 'All-Right':
        return this.qualityChecks;
      case 'RUNS':
        return this.myCheckRuns;
      default: return [];
    }
  }

  getItemIcon(key: string) {
    switch (key) {
      case 'BIM':
        return faCube;
      case 'LOI':
        return faCircleInfo;
      default:
        return faFile;
    }
  }

  get activeOptionUiBlocks(): { messageId: string; blockIndex: number; uiBlock: UiBlock }[] {
    return this.chatService.getActiveOptionUiBlocks(this.surface);
  }

  messagePlaceholder() {
    if (this.surface === 'mycheck') {
      return 'Oder etwas anderes eingeben';
    }
    return 'Frage chatBIC etwas zu deinem Projekt';
  }

  isCompactLayout(): boolean {
    return this.chatService.isFloatingMode && this.surface !== 'mycheck';
  }

  trackByActiveOptionUiBlock(_: number, entry: { messageId: string; blockIndex: number }): string {
    return `${entry.messageId}-${entry.blockIndex}`;
  }

  protected readonly faXmark = faXmark;
  protected readonly faCircleArrowRight = faCircleArrowRight;

  /** Anzeigename eines Prüfergebnisses – derselbe wie in der Berichtsliste. */
  runLabel(run: SimpleCheck): string {
    return this.checkReportService.getCheckName(run);
  }

  selectModuleItem(item: any) {
    if (!this.selectedModule) return;
    // Prüfergebnisse ergänzen die Auswahl (Dateien plus Ergebnis ist eine sinnvolle Frage); die
    // älteren Prüfungen bleiben exklusiv wie bisher.
    if (this.selectedModule.key === 'RUNS') {
      const run = item as SimpleCheck;
      const ref = {
        checkResultId: run.checkResultId,
        instanceId: this.checkReportService.getWorkflowInstanceId(run),
        type: run.checkRuleType,
        name: this.runLabel(run),
      };
      if (!this.session.selectedRuns.some(r => r.checkResultId === ref.checkResultId && r.instanceId === ref.instanceId)) {
        this.session.selectedRuns.push(ref);
      }
      this.closeFolderPopup();
      return;
    }
    this.session.selectedFiles = [];
    this.session.LOI_Check = undefined;
    this.session.BIM_Check = undefined;
    this.session.HOAI_Check = undefined;
    this.session.QUALITY_Check = undefined;
    switch (this.selectedModule.key) {
      case 'BIM':
        this.session.BIM_Check = item;
        break;
      case 'LOI':
        this.session.LOI_Check = item;
        break;
      case 'HOAI':
        this.session.HOAI_Check = item;
        break;
      case 'All-Right':
        this.session.QUALITY_Check = item;
        break;
    }
    this.closeFolderPopup();
  }

  protected readonly faStop = faStop;
  protected readonly faTriangleExclamation = faTriangleExclamation;

  stopAnswer(): void {
    void this.chatService.stopAnswer(this.surface);
  }
  protected readonly faPaperPlaneTop = faPaperPlaneTop;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faCube = faCube;
  protected readonly faPlus = faPlus;

  adjustTextareaHeight() {
    if (!this.textareaRef) return;
    const textarea = this.textareaRef.nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }
  protected readonly faHouse = faHouse;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faFile = faFile;

  closeFolderPopup() {
    this.showFolderPopup = false;
    this.selectedModuleItem = null;
    this.selectedModule = null;
  }

  askQuestion(question: string) {
    const currentChat = this.session.currentChat;
    if (currentChat && currentChat.id != '0') {
      this.chatService.sendMessage(this.surface, currentChat.id, question)
      this.session.question = '';
      setTimeout(() => this.adjustTextareaHeight(), 0);
    } else {
      this.chatService.createChat(this.surface).then(newThreadId => {
        this.chatService.sendMessage(this.surface, newThreadId, question)
        this.session.question = '';
        setTimeout(() => this.adjustTextareaHeight(), 0);

        // Die URL bekommt die Thread-ID, damit ein Reload den Verlauf wiederfindet.
        //
        // Bewusst `Location.replaceState` und NICHT `Router.navigate`: die Chat-Seite ist unter
        // zwei getrennten Routen registriert (`chat` und `chat/:chatId`, siehe routes.config.ts).
        // Ein echter Navigationsvorgang wechselt damit die Route-Konfiguration, und Angular baut
        // `ChatBICComponent` ab und neu auf – mitten im gerade gestarteten Turn. Dessen
        // `ngOnDestroy` ruft `stopListeningForNotifications()`, das alle offenen Antwort-Streams
        // abbestellt; der Neuaufbau leert danach Denkschritte und Nachrichtenliste. Die Antwort
        // entstand serverseitig vollständig, erreichte die Ansicht aber nie.
        //
        // `replaceState` ändert nur die Adresszeile. Das genügt, denn die ID wird ausschliesslich
        // beim Laden der Seite gelesen (`chatIdFromRouting`).
        if (this.chatService.isOnChatPage()) {
          this.location.replaceState(
            `/projects/${this.stateConfigService.getProjectId()}/chat/${newThreadId}`
          );
        }
      })
    }
  }

  removeSelectedItem(item: { type: string }, index: number) {
    if (item.type === 'BIM') {
      this.session.BIM_Check = undefined;
      this.bimcheckChipHovering = false;
    } else if (item.type === 'LOI') {
      this.session.LOI_Check = undefined;
      this.loicheckChipHovering = false;
    } else if (item.type === 'HOAI') {
      this.session.HOAI_Check = undefined;
      this.hoaiChipHovering = false;
    } else if (item.type === 'QUALITY') {
      this.session.QUALITY_Check = undefined;
      this.qualityChipHovering = false;
    } else if (item.type === 'FILES') {
      this.session.selectedFiles.splice(index, 1);
      this.fileChipHovering = false;
    } else if (item.type === 'RUNS') {
      this.session.selectedRuns.splice(index, 1);
    }
  }

  /**
   * Ist das Senden gesperrt, weil chatBIC nicht verfuegbar ist?
   *
   * <p>Absichtlich getrennt von "noch nichts getippt": nur dieser Fall braucht eine Erklaerung,
   * ein leeres Eingabefeld erklaert sich selbst.
   */
  protected get chatBICUnavailable(): boolean {
    return this.chatBICNotEnabled || this.chatService.chatQuota.exceeded;
  }

  /**
   * Begruendung fuer den gesperrten Senden-Button.
   *
   * <p>Zwei getrennte Gruende: die Freischaltung (`featureConfig.chatBIC`) und das ausgeschoepfte
   * Kontingent des Projekts (`ChatService.chatQuota`). Letzteres gehoert in einem
   * Organisationsprojekt der Organisation, sonst dem Projektbesitzer – der Text sagt, wessen.
   */
  protected get sendDisabledTooltip(): string {
    if (!this.chatBICUnavailable) {
      return '';
    }
    if (this.chatBICNotEnabled) {
      return 'chatBIC ist für Ihre Organisation nicht freigeschaltet.';
    }

    const quota = this.chatService.chatQuota;
    const used = quota.used?.toLocaleString('de-DE');
    const limit = quota.limit?.toLocaleString('de-DE');
    const amount = used && limit ? ` (${used} von ${limit} Token)` : '';
    const owner = quota.scope === 'ORGANISATION' ? ' Ihrer Organisation' : '';
    // Das Kontingent gilt je Kalendermonat; resetsAt ist der naechste Monatserste (Backend
    // TokenUsageService, BIC-Frontend#2555).
    const resetsAt = quota.resetsAt
      ? ` Ab dem ${quota.resetsAt.toLocaleDateString('de-DE')} wieder verfügbar.`
      : '';
    return `chatBIC-Kontingent${owner} aufgebraucht${amount}.${resetsAt}`;
  }

  /** Nicht freigeschaltet (`feature_config.chat_bic`) – unabhängig vom Verbrauch. */
  private get chatBICNotEnabled(): boolean {
    return (this.stateConfigService.getFeatureConfig()?.chatBIC ?? true) === false;
  }

  protected get sendTooltip(): string {
    return this.sendDisabledTooltip || 'Nachricht abschicken';
  }
}
